import React from "react";
import {I18nString} from "../../models/lang/i18nString";
import {range} from "../../models/settings/menu";
import {useGlobalContext, useUserContext} from "../../hooks/globalContext";
import {NumberInput, NumberInputRange} from "../input/numberInput";
import {TextI18n} from "../input/textI18n";

export interface ChargeItem {
    type: number;
    value: number | range[];
    label: I18nString | null;
}

interface ChargesEditorProps {
    charges: ChargeItem[];
    onChange: (charges: ChargeItem[]) => void;
}

export function ChargesEditor(props: ChargesEditorProps) {
    const {i18n} = useGlobalContext();
    const {menu, user} = useUserContext();
    const options = [i18n.fixed(), i18n.percent(), i18n.range_charge()];

    function setCharges(next: ChargeItem[]) {
        props.onChange(next);
    }

    function handleTypeChange(index: number, type: number) {
        setCharges(props.charges.map((item, i) => {
            if (i !== index) return item;
            if (type === 2) {
                return {...item, type: 2, value: [{from: 0, to: 0, value: 0}]};
            }
            return {...item, type, value: 0};
        }));
    }

    function handleAddRange() {
        setCharges(props.charges.map((item) => {
            if (item.type === 2) {
                return {...item, value: [...(item.value as range[]), {from: 0, to: 0, value: 0}]};
            }
            return item;
        }));
    }

    function handleRemoveRange(rangeIndex: number) {
        setCharges(props.charges.map((item) => {
            if (item.type === 2) {
                return {...item, value: (item.value as range[]).filter((_, i) => i !== rangeIndex)};
            }
            return item;
        }));
    }

    function handleValueChange(index: number, value: number | range, rangeIndex?: number) {
        const target = props.charges[index];
        switch (target.type) {
            case 0:
                setCharges(props.charges.map((item, i) => i === index ? {
                    ...item, value: (value as number) * 100,
                } : item));
                break;
            case 1:
                setCharges(props.charges.map((item, i) => i === index ? {
                    ...item, value: value as number,
                } : item));
                break;
            case 2:
                setCharges(props.charges.map((item, i) => {
                    if (i !== index) return item;
                    return {
                        ...item,
                        value: (item.value as range[]).map((r, ri) => ri === rangeIndex ? {...value as range} : r),
                    };
                }));
                break;
        }
    }

    function handleLabelChange(index: number, label: I18nString) {
        setCharges(props.charges.map((item, i) => i === index ? {...item, label} : item));
    }

    function handleRemoveCharge(index: number) {
        setCharges(props.charges.filter((_, i) => i !== index));
    }

    function handleAddCharge() {
        setCharges([...props.charges, {type: 0, value: 0, label: {}}]);
    }

    const hasRangeCharge = !!props.charges.find((v) => v.type === 2);

    return (
        <div className={"control"}>
            <label className="control__label">{i18n.payment_supplement()}</label>
            {props.charges.map((v, index) => (
                <div className="row add-price-tuple" data-gutter="15"
                     key={"charge-" + index.toString()}>
                    <div className="col-4">
                        <div className="control">
                            <label className="control__label">{i18n.type()}</label>
                            <select className="control__input"
                                    value={v.type}
                                    onChange={(e) => handleTypeChange(index, parseInt(e.currentTarget.value))}>
                                {options.map((option, oi) => (
                                    <option
                                        key={oi}
                                        value={oi}
                                        disabled={oi === 2 && hasRangeCharge && v.type !== 2}
                                    >
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {v.type === 2
                        ? (v.value as range[]).map((r, rangeIndex) => (
                            <div style={{display: "flex"}} key={rangeIndex}>
                                <NumberInputRange
                                    fromLabel={i18n.from()}
                                    toLabel={i18n.to()}
                                    label={i18n.value()}
                                    isRequired={true}
                                    value={r}
                                    onChange={(val) => handleValueChange(index, val, rangeIndex)}
                                />
                                <div className="col-1">
                                    <button type="button" className="form__remove-button"
                                            onClick={() => handleRemoveRange(rangeIndex)}>
                                        <RemoveIcon/>
                                    </button>
                                </div>
                            </div>
                        ))
                        : (
                            <div className="col-3">
                                <NumberInput label={i18n.value()}
                                             value={v.type === 0 ? (v.value as number) / 100 : (v.value as number)}
                                             onChange={(int) => handleValueChange(index, int)}/>
                            </div>
                        )
                    }

                    <div className="col-4">
                        <TextI18n values={v.label} isStaff={user?.isStaff || false}
                                  setValues={(val) => handleLabelChange(index, val)}
                                  lang={menu.i18n.defaultLang}
                                  allLang={menu.i18n.otherLangs || []}
                                  label={i18n.title()}
                                  isRequired={true}
                        />
                    </div>

                    <div className="col-1">
                        <button type="button" className="form__remove-button"
                                onClick={() => handleRemoveCharge(index)}>
                            <RemoveIcon/>
                        </button>
                    </div>

                    {v.type === 2 && index === props.charges.length - 1 && (
                        <div className="row add-price-tuple" data-gutter="15" style={{width: "100%"}}>
                            <a href="#"
                               style={{paddingTop: "10px", paddingBottom: "10px", paddingLeft: "10px"}}
                               onClick={(e) => {
                                   e.preventDefault();
                                   handleAddRange();
                               }}>
                                {i18n.add_addit_range()}
                            </a>
                        </div>
                    )}
                </div>
            ))}

            <div className="row add-price-tuple" data-gutter="15">
                <a href="#" style={{paddingTop: "10px", paddingLeft: "10px"}}
                   onClick={(e) => {
                       e.preventDefault();
                       handleAddCharge();
                   }}>
                    {i18n.add_addit_price()}
                </a>
            </div>
        </div>
    );
}

function RemoveIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M18.5109 2.3814H14.3669C14.3669 2.19883 14.3307 2.01807 14.2605 1.84957C14.1902 1.68108 14.0872 1.52818 13.9575 1.39973C13.8277 1.27128 13.6738 1.16983 13.5046 1.10123C13.3354 1.03262 13.1543 0.998243 12.9718 1.00007H10.2367C10.0542 0.998243 9.87308 1.03262 9.70389 1.10123C9.5347 1.16983 9.38079 1.27128 9.25106 1.39973C9.12132 1.52818 9.01834 1.68108 8.94806 1.84957C8.87778 2.01807 8.84159 2.19883 8.8416 2.3814H4.69762C4.51445 2.3814 4.33877 2.45416 4.20925 2.58369C4.07972 2.71321 4.00696 2.88888 4.00696 3.07206C4.00696 3.44502 4.31085 3.76272 4.69762 3.76272H18.5109C18.6941 3.76272 18.8697 3.68996 18.9993 3.56043C19.1288 3.43091 19.2016 3.25523 19.2016 3.07206C19.2016 2.88888 19.1288 2.71321 18.9993 2.58369C18.8697 2.45416 18.6941 2.3814 18.5109 2.3814ZM8.8416 9.97869C8.8416 9.59192 9.15931 9.28803 9.53227 9.28803C9.91904 9.28803 10.2229 9.60574 10.2229 9.97869V18.2667C10.2229 18.4498 10.1502 18.6255 10.0206 18.755C9.89112 18.8846 9.71544 18.9573 9.53227 18.9573C9.34909 18.9573 9.17342 18.8846 9.04389 18.755C8.91437 18.6255 8.8416 18.4498 8.8416 18.2667V9.97869ZM12.9856 9.97869C12.9856 9.60574 13.3033 9.28803 13.6762 9.28803C14.063 9.28803 14.3669 9.60574 14.3669 9.97869V18.2667C14.3669 18.4498 14.2941 18.6255 14.1646 18.755C14.0351 18.8846 13.8594 18.9573 13.6762 18.9573C13.4931 18.9573 13.3174 18.8846 13.1879 18.755C13.0584 18.6255 12.9856 18.4498 12.9856 18.2667V9.97869Z"
                fill="#8392A5"/>
            <path
                d="M17.8213 6.52539L16.4262 21.5957C16.4262 21.6509 16.3433 21.72 16.2881 21.72H6.92267C6.8536 21.72 6.78453 21.6371 6.78453 21.5957L5.38939 6.52539H17.7937H17.8213ZM17.9318 5.14407H5.25126C5.0757 5.14298 4.9019 5.17909 4.7413 5.25002C4.5807 5.32095 4.43695 5.42509 4.3195 5.55558C4.20206 5.68608 4.11358 5.83997 4.0599 6.00713C4.00622 6.17429 3.98856 6.35092 4.00807 6.52539L5.41702 21.72C5.48609 22.4797 6.16294 23.1013 6.93648 23.1013H16.2743C16.6526 23.0967 17.0162 22.9536 17.2962 22.6991C17.5762 22.4446 17.7532 22.0962 17.7937 21.72L19.2027 6.52539C19.2222 6.35092 19.2045 6.17429 19.1508 6.00713C19.0972 5.83997 19.0087 5.68608 18.8912 5.55558C18.7738 5.42509 18.63 5.32095 18.4694 5.25002C18.3088 5.17909 18.135 5.14298 17.9595 5.14407H17.9318Z"
                fill="#8392A5"/>
        </svg>
    );
}
